// WebRTC helpers for end-to-end calls.
//
// ICE servers are fetched from the backend (`GET /webrtc/credentials`). The
// TURN credentials are a short-lived HMAC over (expiry, account_id) signed
// with the shared TURN secret. We cache them in memory and refresh five
// minutes before expiry. `PUBLIC_STUN` is the fallback if the backend is
// unreachable when a call is being placed.

import { api } from './api';

export const PUBLIC_STUN: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

// Backward-compatible alias; old callers reading webrtc.ICE_SERVERS get STUN
// fallback. New code should use getIceServers().
export const ICE_SERVERS: RTCIceServer[] = PUBLIC_STUN;

let cached: { iceServers: RTCIceServer[]; expiresAt: number } | null = null;

export async function getIceServers(): Promise<RTCIceServer[]> {
  if (cached && Date.now() < cached.expiresAt - 5 * 60 * 1000) {
    return cached.iceServers;
  }
  try {
    const r = await api.iceServers();
    cached = {
      iceServers: r.iceServers,
      expiresAt: new Date(r.expires_at).getTime(),
    };
    return r.iceServers;
  } catch (err) {
    console.warn('[webrtc] TURN credentials fetch failed, falling back to public STUN:', (err as Error).message);
    return PUBLIC_STUN;
  }
}

export async function getLocalMedia(video: boolean): Promise<MediaStream> {
  const constraints: MediaStreamConstraints = {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    } as MediaTrackConstraints,
    video: video
      ? ({
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        } as MediaTrackConstraints)
      : false,
  };
  return navigator.mediaDevices.getUserMedia(constraints);
}

export async function createPeerConnection(
  onIceCandidate: (c: RTCIceCandidate) => void,
  onTrack: (s: MediaStream) => void,
  onConnectionStateChange: (s: RTCPeerConnectionState) => void,
): Promise<RTCPeerConnection> {
  const iceServers = await getIceServers();
  const pc = new RTCPeerConnection({ iceServers });
  pc.onicecandidate = (ev) => {
    if (ev.candidate) onIceCandidate(ev.candidate);
  };
  pc.ontrack = (ev) => {
    if (ev.streams && ev.streams[0]) onTrack(ev.streams[0]);
  };
  pc.onconnectionstatechange = () => onConnectionStateChange(pc.connectionState);
  return pc;
}
